/**
 * EmbeddingModelWidget — Redesigned for F-LLMCFG Packet 2.
 *
 * AC-7: Two label-less dropdowns (provider → model) showing saved selections.
 *   "Change provider"/"Change model" labels, info lines, and the old Change Provider
 *   button are gone.
 *
 * AC-8: Selecting a provider repopulates the model dropdown; nothing is persisted
 *   by selection alone (reload restores saved values).
 *
 * AC-9: Change Model button → determines variant:
 *   - warning: orgModel !== null OR existing embeddings (chunkCount > 0)
 *   - info:    first-time org (no orgModel, no embeddings)
 *   Cancel → nothing saved, no backfill. Confirm → POST embedding-model/change
 *   with {newModel, newProvider, confirm:true} → persists AND auto-starts backfill.
 *
 * AC-10: BackfillWidget deleted; Run Backfill button lives here.
 *
 * LAYERING NOTE (AILLM-7): Lives in components/roros/settings/ but imported by
 * routes/organization/AISettings.tsx. Intentional shallow coupling documented in P1.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Button, Select,
} from '@/components/ui';
import { Modal } from '@/components/ui/Modal/Modal';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { Database, AlertTriangle, Loader2, Info, RefreshCw } from 'lucide-react';
import { useAiConfigStatus } from '@/hooks/roros/useAiConfigStatus';
import { PROVIDER_LABELS } from '@/components/ai/aiProviderConstants';

interface ModelPreview {
  sourceCount: number;
  chunkCount: number;
  estimatedCostUsd: number;
  estimatedDurationMin: number;
}

interface CurrentModel {
  orgModel: string | null;
  systemModel: string | null;
  effectiveModel: string | null;
}

interface EmbeddingProvider {
  provider: string;
  models: string[];
}

export function EmbeddingModelWidget() {
  const { activeOrgId } = useOrgStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.MANAGE_KNOWLEDGE);
  const queryClient = useQueryClient();

  // AC-BYOK-6: detect whether the org has any embedding-capable provider configured.
  const { hasEmbeddingCapableProvider, isLoading: isStatusLoading } = useAiConfigStatus();

  // AC-7/8: User's current dropdown selections (not persisted until confirm)
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');

  // AC-9: dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogVariant, setDialogVariant] = useState<'warning' | 'info'>('info');
  const [preview, setPreview] = useState<ModelPreview | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────────

  // Saved provider
  const { data: currentProviderData } = useQuery({
    queryKey: ['rag-embedding-provider', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-provider`);
      if (!res.ok) return { provider: 'openai' };
      const json = await res.json();
      return json.data as { provider: string };
    },
    enabled: !!activeOrgId && canManage,
  });

  // Saved model (used to determine warning vs info variant)
  const { data: currentModelData, refetch: refetchModel, isLoading: isModelLoading } = useQuery({
    queryKey: ['rag-embedding-model', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-model`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || 'Failed to fetch model');
      }
      const json = await res.json();
      return json.data as CurrentModel;
    },
    enabled: !!activeOrgId && canManage,
  });

  // Available embedding providers
  const { data: availableProviders, isLoading: isProvidersLoading } = useQuery({
    queryKey: ['rag-embedding-providers', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-providers`);
      if (!res.ok) return [{ provider: 'openai', models: [] }];
      const json = await res.json();
      return json.data as EmbeddingProvider[];
    },
    enabled: !!activeOrgId && canManage,
  });

  // Available models for selectedProvider (AC-8: keyed on selectedProvider not saved)
  const { data: availableModels, isLoading: isModelsLoading } = useQuery({
    queryKey: ['rag-embedding-models', activeOrgId, selectedProvider],
    queryFn: async () => {
      // Provider-aware: use selectedProvider in the request
      // The backend reads the stored rag_embedding_provider, but we're passing our selection
      // as a transient query param so the list is repopulated for the selected provider.
      // For now: if selectedProvider differs from saved, we map it via the providers list.
      const providers = availableProviders ?? [];
      const providerEntry = providers.find((p) => p.provider === selectedProvider);
      if (providerEntry?.models?.length) {
        // Use the models embedded in the providers list (for embedding-capable providers)
        return providerEntry.models.map((id) => ({ id, label: id }));
      }
      // FW-10: pass selectedProvider as ?provider= query param so the backend returns
      // models for the SELECTED provider (not the saved one).  Without this, switching
      // the provider dropdown would still show the old provider's model list.
      const url = `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-models${selectedProvider ? `?provider=${encodeURIComponent(selectedProvider)}` : ''}`;
      const res = await apiFetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || t('Failed to load embedding models'));
      }
      const json = await res.json();
      return json.data as Array<{ id: string; label: string }>;
    },
    enabled: !!activeOrgId && canManage && !!selectedProvider,
  });

  // ── Sync saved values → local selection state on initial load ─────────────
  // When saved provider/model load, initialize the dropdowns to saved values.
  useEffect(() => {
    if (currentProviderData?.provider && !selectedProvider) {
      setSelectedProvider(currentProviderData.provider);
    }
  }, [currentProviderData?.provider]);

  useEffect(() => {
    if (currentModelData?.effectiveModel && !selectedModel) {
      setSelectedModel(currentModelData.effectiveModel);
    }
  }, [currentModelData?.effectiveModel]);

  // When provider changes reset model selection (AC-8)
  const handleProviderChange = (newProv: string) => {
    setSelectedProvider(newProv);
    setSelectedModel(''); // reset model when provider repopulates
  };

  // ── Mutations ────────────────────────────────────────────────────────────────

  // Load preview + determine variant + open dialog
  const loadPreviewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-model/preview`);
      if (!res.ok) throw new Error(t('Failed to load impact preview'));
      const json = await res.json();
      return json.data as ModelPreview;
    },
    onSuccess: (data) => {
      setPreview(data);
      // AC-9: warning if model was previously set OR embeddings already exist
      const orgModel = currentModelData?.orgModel ?? null;
      const variant: 'warning' | 'info' =
        orgModel !== null || data.chunkCount > 0 ? 'warning' : 'info';
      setDialogVariant(variant);
      setShowDialog(true);
    },
    onError: (err: unknown) =>
      showToast.error((err instanceof Error ? err.message : null) || t('Failed to load impact preview')),
  });

  // Confirm: persist provider + model + starts backfill automatically (AC-9)
  const confirmChangeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/embedding-model/change`,
        {
          method: 'POST',
          body: JSON.stringify({
            newModel: selectedModel.trim(),
            newProvider: selectedProvider.trim(),
            confirm: true,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || t('Failed to change embedding model'));
      }
      return res.json();
    },
    onSuccess: () => {
      setShowDialog(false);
      refetchModel();
      queryClient.invalidateQueries({ queryKey: ['rag-embedding-provider', activeOrgId] });
      // Backfill is started automatically on the backend (re-embed events emitted)
      showToast.success(t('Embedding model updated. Re-indexing started automatically.'));
    },
    onError: (err: unknown) =>
      showToast.error((err instanceof Error ? err.message : null) || t('Failed to change embedding model')),
  });

  // AC-10: Run Backfill button (manual catch-up for items that failed indexing)
  const backfillMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/backfill`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(t('Failed to start backfill'));
      return res.json();
    },
    onSuccess: () => showToast.success(t('Backfill started. This may take several minutes.')),
    onError: (err: unknown) =>
      showToast.error((err instanceof Error ? err.message : null) || t('Failed to start backfill')),
  });

  // Rule of Hooks: early-return MUST come after all hook declarations
  if (!canManage) return null;

  const noEmbeddingProvider = !isStatusLoading && !hasEmbeddingCapableProvider;

  const savedProvider = currentProviderData?.provider ?? 'openai';
  const savedModel = currentModelData?.effectiveModel ?? null;

  // FW-11b: use shared PROVIDER_LABELS constant (no inline ternary duplication)
  const providerOptions = (availableProviders ?? []).map((p) => ({
    value: p.provider,
    label: PROVIDER_LABELS[p.provider] ?? p.provider,
  }));

  const modelOptions = (availableModels ?? []).map((m) => ({
    value: m.id,
    label: m.label ?? m.id,
  }));

  // AC-8: Change Model is enabled only when a model is selected and it differs from saved
  const modelChanged = selectedModel !== '' && (selectedModel !== savedModel || selectedProvider !== savedProvider);

  return (
    <>
      <Card data-testid="embedding-model-widget">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary-600" />
            {t('Embedding Model')}
          </CardTitle>
          <CardDescription>{t('Embedding provider and model used for vector search.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* AC-BYOK-6: no embedding-capable provider guidance */}
          {noEmbeddingProvider && (
            <div
              className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800"
              data-testid="no-embedding-provider-guidance"
              role="status"
            >
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('Add an OpenAI or Google provider to enable knowledge search')}
              </p>
            </div>
          )}

          {/* AC-7: Two label-less provider + model dropdowns */}
          <div className="flex flex-col gap-3" data-testid="embedding-dropdowns">
            {/* Provider dropdown — label-less (AC-7) */}
            <Select
              value={selectedProvider || savedProvider}
              onValueChange={handleProviderChange}
              options={providerOptions}
              placeholder={isProvidersLoading ? t('Loading…') : t('Select provider')}
              disabled={isProvidersLoading || noEmbeddingProvider}
              data-testid="embedding-provider-select"
              aria-label={t('Embedding provider')}
            />

            {/* Model dropdown — label-less (AC-7), repopulates on provider change (AC-8) */}
            <Select
              value={selectedModel}
              onValueChange={setSelectedModel}
              options={modelOptions}
              placeholder={isModelsLoading ? t('Loading…') : (savedModel ? savedModel : t('Select embedding model'))}
              disabled={isModelsLoading || noEmbeddingProvider || !selectedProvider}
              data-testid="embedding-model-select"
              aria-label={t('Embedding model')}
            />
          </div>

          {/* Change Model button + Run Backfill button (AC-10) */}
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => loadPreviewMutation.mutate()}
              disabled={!modelChanged || loadPreviewMutation.isPending || noEmbeddingProvider}
              isLoading={loadPreviewMutation.isPending}
              data-testid="change-model-btn"
              title={noEmbeddingProvider ? t('Add an OpenAI or Google provider to enable knowledge search') : undefined}
            >
              {t('Change Model')}
            </Button>

            {/* AC-10: Run Backfill — moved from BackfillWidget into this widget */}
            <Button
              variant="outline"
              onClick={() => backfillMutation.mutate()}
              disabled={backfillMutation.isPending || noEmbeddingProvider}
              isLoading={backfillMutation.isPending}
              data-testid="run-backfill-btn"
              title={noEmbeddingProvider ? t('Add an OpenAI or Google provider to enable knowledge search') : undefined}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              {t('Run Backfill')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AC-9: Warning dialog (existing embeddings / previously set model) */}
      <Modal
        isOpen={showDialog && dialogVariant === 'warning'}
        onClose={() => !confirmChangeMutation.isPending && setShowDialog(false)}
        title={t('Change Embedding Model?')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={confirmChangeMutation.isPending}
              data-testid="embedding-change-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => confirmChangeMutation.mutate()}
              isLoading={confirmChangeMutation.isPending}
              data-testid="embedding-change-confirm-btn"
            >
              {t('Confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div
            className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800"
            data-testid="embedding-warning-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t('All documents must be re-embedded with the new model. Items will be temporarily excluded from search until re-indexing completes.')}
            </p>
          </div>

          {preview && (
            <div className="space-y-2 text-sm">
              <p className="font-medium">{t('Impact of this change:')}</p>
              <div className="grid grid-cols-2 gap-2 p-3 bg-muted/50 rounded-lg">
                <span className="text-muted-foreground">{t('Sources to re-index')}</span>
                <span className="font-medium">{preview.sourceCount}</span>
                <span className="text-muted-foreground">{t('Chunks to re-embed')}</span>
                <span className="font-medium">{preview.chunkCount}</span>
                <span className="text-muted-foreground">{t('Estimated cost (USD)')}</span>
                <span className="font-medium">${preview.estimatedCostUsd.toFixed(4)} {t('(estimated)')}</span>
                <span className="text-muted-foreground">{t('Estimated duration')}</span>
                <span className="font-medium">~{preview.estimatedDurationMin} {t('min (estimated)')}</span>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {t('New model')}: <span className="font-mono font-medium">{selectedModel}</span>
          </p>
        </div>
      </Modal>

      {/* AC-9: Info dialog (first-time selection, no prior embeddings) */}
      <Modal
        isOpen={showDialog && dialogVariant === 'info'}
        onClose={() => !confirmChangeMutation.isPending && setShowDialog(false)}
        title={t('Enable Embedding?')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={confirmChangeMutation.isPending}
              data-testid="embedding-change-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => confirmChangeMutation.mutate()}
              isLoading={confirmChangeMutation.isPending}
              data-testid="embedding-change-confirm-btn"
            >
              {t('OK')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4" data-testid="embedding-info-banner">
          <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('Existing documents will now be embedded and backfill will start automatically.')}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('Selected model')}: <span className="font-mono font-medium">{selectedModel}</span>
          </p>
        </div>
      </Modal>
    </>
  );
}
